import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { Section } from "views/applications-id-report/components/Section";
import { Stat } from "views/applications-id-report/components/Stat";
import { has, toMoney } from "shared/utils";
import moment from "moment";

import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { Show } from "shared/components";
import { CreditCardIcon, Home05Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { CombinedIncomeReport, EditIncomeModalData } from "./types";
import { NetIncomeChart } from "./slices/NetIncomeGraphSlice";
import { ApplicationStatus, IncomeType, VerificationNames } from "shared/types";
import { useQuery } from "@tanstack/react-query";
import { VerificationReport } from "types/landlord/reports";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import api from "api/landlord/applications";
import { Loader } from "shared/components/Loader";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { useState } from "react";
import { Applicant } from "types/application";
import { EditIncomeModal } from "./components/EditIncomeModal";
import { mapNetIncomeAnalysis } from "./helpers";
import { SummaryUpdated } from "./components/SummaryUpdated";
import { ExpandableTable } from "./components/UniversalTable";
import { activityLogsQueryKey } from "shared/queries/useActivityLogsController";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { instance } from "api/apiBase";
import queryString from "qs";
import { AdjustedApplicationRentActivityLog } from "views/applications-id-report/components/AdjustedApplicationRentIncomeSection";
import { ReverifyIncomeModal } from "./components/ReverifyIncomeModal";
import { toast } from "react-toastify";
import { IncomeSlice } from "./slices/TablesSlice/IncomeSlice";
import { DocumentWarnings } from "./slices/DocumentWarningsSlice";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { CriticalDataMissingWarning } from "./components/CriticalDataMissingWarning";
import {
  getAdditionalDataPerVerificationType,
  isIncomeVerificationSkipped
} from "./utils";
import { ProjectedDebtToIncome } from "./components/ProjectedDebtToIncome";
import { NoIncomeSection } from "./components/NoIncomeSection";

export const CombinedIncomeReportSlice = ({
  verificationId,
  applicantId,
  applicationId,
  hasCreditReport,
  applicant,
  applicationStatus
}: {
  verificationId?: number;
  applicantId?: number;
  applicationId?: number;
  hasCreditReport?: boolean;
  applicant: Applicant;
  applicationStatus: ApplicationStatus;
}) => {
  const [showReverifyIncomeModal, setShowReverifyIncomeModal] = useState<
    number | false
  >(false);

  const [showEditIncomeModal, setShowEditIncomeModal] = useState<
    false | EditIncomeModalData
  >(false);

  const {
    data: reportData,
    isError,
    isLoading,
    refetch
  } = useQuery<VerificationReport<CombinedIncomeReport>>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.PetScreeningReport,
      applicationId: Number(applicationId || 0),
      applicantId: Number(applicantId || 0),
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          Number(applicationId || 0),
          Number(applicantId || 0),
          verificationId || 0
        )
        .then((data) => data.verification)
  });

  const applicationRentActivityLogsMeta = useQuery<
    AdjustedApplicationRentActivity[]
  >({
    queryKey: activityLogsQueryKey(applicant.application_id),
    queryFn: () => {
      const params = {
        events: [
          "application_rent_adjusted",
          "application_rent_automatically_adjusted"
        ]
      };
      const url =
        `portal/v1/applications/${applicant.application_id}/activity_logs?` +
        queryString.stringify(params, { arrayFormat: "brackets" });
      return instance.get(url).then(({ data }) => data.activity_logs);
    },
    enabled: true
  });

  const data = reportData?.report;
  const settings = reportData?.settings;

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <Section maxWidth="none" sectionTitle="Income summary">
        <ReportException
          title="Failed to load income report"
          applicationStatus={applicationStatus}
          applicationId={applicationId || "0"}
        />
      </Section>
    );

  if (!data || !Object.keys(data).length) return null;

  const noIncome = data.incomes.find((income) => income.type === "no_income");

  if (noIncome || data.incomes.length === 0) {
    const housingAssistance = data.incomes.find(
      (income) => income.type === "housing_assistance"
    );

    return (
      <NoIncomeSection
        noIncome={noIncome}
        housingAssistance={housingAssistance}
        applicant={applicant}
        verificationId={verificationId}
      />
    );
  }

  const summary = data.total_summary,
    debts = summary.debts,
    netIncomeData = data.net_income_analysis,
    incomes = data.incomes,
    bankStatements = data.bank_statements;

  const isSkipped = incomes.every(isIncomeVerificationSkipped);

  const netMonthlyIncome =
    data.total_overrides?.net_monthly_income !== undefined
      ? toMoney(data.total_overrides.net_monthly_income)
      : toMoney(summary.verified_net_monthly_total);
  const debtGrossIncomeRatio =
    typeof summary.debt_gross_income_ratio === "undefined" ||
    summary.debt_gross_income_ratio === null
      ? "-"
      : summary.debt_gross_income_ratio + "%";

  return (
    <div>
      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicationId || "0"}
        title="Income history"
        applicantId={applicantId || 0}
        name={VerificationNames.CombinedIncome}
        reportDate={new Date()}
      />

      {verificationId ? <PrintReady verificationId={verificationId} /> : null}

      <Section
        sectionTitle="Income summary"
        maxWidth="100%"
        // Hide edit total income
        // rightElement={
        //   <Button
        //     variant={ButtonVariant.TEXT}
        //     size={ButtonSize.SM}
        //     onClick={() =>
        //       setShowEditIncomeModal({
        //         grossMonthlyIncomeCents:
        //           data.total_overrides?.gross_monthly_income !== undefined
        //             ? data.total_overrides.gross_monthly_income * 100
        //             : summary.self_reported_gross_monthly_total * 100,
        //         netMonthlyIncomeCents:
        //           data.total_overrides?.net_monthly_income !== undefined
        //             ? data.total_overrides.net_monthly_income * 100
        //             : summary.verified_net_monthly_total * 100,
        //         hasNetMonthlyIncome:
        //           typeof summary.verified_net_monthly_total === "number",
        //         isTotal: true
        //       })
        //     }
        //   >
        //     Edit total income
        //   </Button>
        // }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Stat
            title="Verified gross monthly"
            value={
              isSkipped ? "-" : toMoney(summary.verified_gross_monthly_total)
            }
          />
          <Stat
            title="Verified net monthly"
            value={isSkipped ? "-" : netMonthlyIncome}
          />
          <Show if={hasCreditReport}>
            <Stat
              title="Debt to verified gross income ratio"
              value={isSkipped ? "-" : debtGrossIncomeRatio}
            />
          </Show>

          <Stat
            title="Self-reported gross monthly"
            value={
              data.total_overrides?.gross_monthly_income !== undefined
                ? toMoney(data.total_overrides.gross_monthly_income)
                : toMoney(summary.self_reported_gross_monthly_total)
            }
          />
          <Stat
            title="Monthly assistance amount"
            value={
              summary.monthly_housing_assistance
                ? toMoney(summary.monthly_housing_assistance)
                : "-"
            }
          />
          <Stat
            title="Verified income sources"
            value={
              summary.verified_incomes_counter.verified +
              " / " +
              (summary.verified_incomes_counter.total +
                incomes.filter((inc) => inc.type === IncomeType.OfferLetter)
                  .length)
            }
          />
        </div>
        <Show
          if={
            has(data.total_overrides?.gross_monthly_income) ||
            has(data.total_overrides?.net_monthly_income)
          }
        >
          <SummaryUpdated
            changes={data.total_overrides}
            original={{
              gross: summary.self_reported_gross_monthly_total,
              net: summary.verified_net_monthly_total
            }}
          />
        </Show>
        <CriticalDataMissingWarning incomes={incomes} />
      </Section>

      <DocumentWarnings incomes={incomes} bankStatements={bankStatements} />

      <Section sectionTitle="Net income analysis" maxWidth="1100px">
        <NetIncomeChart data={netIncomeData} />

        <div className="mt-[60px]">
          <ExpandableTable
            hideHeader
            rowClassName="items-center pr-4"
            headerRowClassName="text-content-1000 text-sm font-medium"
            columns={[
              {
                header: "month",
                accessor: (row) => moment(row.month).format("MMMM, YYYY"),
                widthClass: "w-full"
              },
              {
                header: "total",
                accessor: (row) => toMoney(row.total),
                widthClass: "w-[120px] shrink-0"
              }
            ]}
            rows={mapNetIncomeAnalysis(netIncomeData)}
            renderExpanded={(original) => {
              return (
                <div className="bg-background-900 border-t border-alpha-100 px-5">
                  {original.income
                    .toSorted(
                      (a, b) =>
                        new Date(a.month).getTime() -
                        new Date(b.month).getTime()
                    )
                    .map((i, iIdx) => {
                      return (
                        <div
                          key={iIdx}
                          className="border-l !text-sm border-content-100 border-dashed h-[72px] flex items-center w-full relative"
                        >
                          {/* is Last idx */}
                          <Show if={iIdx === original.income.length - 1}>
                            <div className="bg-background-900 w-1 h-9 absolute -left-0.5 translate-y-1/2" />
                          </Show>
                          <div className="flex items-center w-full">
                            <div className="flex items-center shrink-0">
                              <div className="w-6 border-t border-content-100 border-dashed h-px" />
                              <div className="bg-content-100 h-1.5 w-1.5 rounded-full" />
                            </div>

                            <p className="ml-3 text-sm font-medium w-[140px]">
                              {moment(i.month).format("ll")}
                            </p>

                            <div className="flex justify-between items-center ml-4 flex-1">
                              <p className="flex items-center gap-2">
                                <span
                                  className="rounded-full block h-2 w-2 shrink-0"
                                  style={{
                                    backgroundColor: i.color
                                  }}
                                ></span>
                                {i.company}
                              </p>
                              <p>{toMoney(i.income)}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            }}
          />
        </div>
      </Section>
      {incomes.map((income, index) => {
        return (
          <IncomeSlice
            key={index}
            income={income}
            applicationId={applicationId}
            applicationStatus={applicationStatus}
            settings={settings}
            additionalData={getAdditionalDataPerVerificationType(data, income)}
            setShowEditIncomeModal={setShowEditIncomeModal}
            setShowReverifyIncomeModal={setShowReverifyIncomeModal}
            isManualApplicant={applicant.is_manual}
            applicant={applicant}
          />
        );
      })}

      <Show
        if={
          summary.debt_gross_income_ratio ||
          summary.debt_net_income_ratio ||
          summary.debt_self_attested_gross_income_ratio
        }
      >
        <Section sectionTitle="Projected debt to income ratio" maxWidth="none">
          <div className="space-y-7 py-8">
            <ProjectedDebtToIncome
              totalMonthlyDebt={summary.total_monthly_debt}
              monthlyIncome={summary.verified_gross_monthly_total}
              monthlyIncomeLabel="Verified gross monthly income"
              debtToIncomeRatio={summary.debt_gross_income_ratio}
              debtToIncomeRatioLabel="Debt to gross income ratio"
            />
            <ProjectedDebtToIncome
              totalMonthlyDebt={summary.total_monthly_debt}
              monthlyIncome={summary.verified_net_monthly_total}
              monthlyIncomeLabel="Verififed net monthly income"
              debtToIncomeRatio={summary.debt_net_income_ratio}
              debtToIncomeRatioLabel="Debt to net income ratio"
            />
            <ProjectedDebtToIncome
              totalMonthlyDebt={summary.total_monthly_debt}
              monthlyIncome={summary.self_reported_gross_monthly_total}
              monthlyIncomeLabel="Self-reported gross monthly income"
              debtToIncomeRatio={summary.debt_self_attested_gross_income_ratio}
              debtToIncomeRatioLabel="Debt to gross income ratio"
            />
          </div>
          <Show if={debts.length > 0}>
            <ContentTitle
              title="Monthly debt includes"
              contentClassName="flex gap-4 flex-col pb-6 pt-5"
              titleVariant="medium"
            >
              {debts.map((debt) => {
                return (
                  <div
                    className="flex justify-between items-center"
                    key={debt.name + debt.amount}
                  >
                    <div className="flex gap-4 items-center">
                      {debt.name === "listed_rent_amount" ? (
                        <HugeiconsIcon
                          icon={Home05Icon}
                          size={20}
                          strokeWidth={1.5}
                          className="text-content-400"
                        />
                      ) : (
                        <HugeiconsIcon
                          icon={CreditCardIcon}
                          size={20}
                          strokeWidth={1.5}
                          className="text-content-400"
                        />
                      )}

                      <div className="space-y-2 text-content-400">
                        <p className="text-content-500 text-sm font-medium first-letter:uppercase">
                          {debt.name === "listed_rent_amount"
                            ? "Application rent"
                            : debt.name.replaceAll("_", " ") || "-"}
                        </p>
                        <p className="text-xs">{debt.description || "-"}</p>
                      </div>
                    </div>

                    <p className="text-content-1000 font-medium text-sm">
                      {toMoney(debt.amount.cents / 100)}
                    </p>
                  </div>
                );
              })}
            </ContentTitle>
          </Show>

          <div className="border-b border-alpha-40 md:px-6 pl-4" />
        </Section>
      </Show>

      <div className="space-y-8 p-6 pt-0 text-sm">
        {applicationRentActivityLogsMeta.data?.map((activityItem, index) => (
          <AdjustedApplicationRentActivityLog
            key={index}
            activityItem={activityItem}
          />
        ))}
      </div>

      <Show if={Boolean(showEditIncomeModal)}>
        <EditIncomeModal
          onSuccess={refetch}
          applicationId={Number(applicationId || 0)}
          applicant={applicant}
          verificationId={verificationId || 0}
          opened={Boolean(showEditIncomeModal)}
          onClose={() => setShowEditIncomeModal(false)}
          {...(typeof showEditIncomeModal === "object"
            ? showEditIncomeModal
            : {})}
        />
      </Show>

      {typeof showReverifyIncomeModal === "boolean" ? null : (
        <ReverifyIncomeModal
          onSuccess={() => {
            toast.success("Re-verification requested successfully");
            refetch();
            setShowReverifyIncomeModal(false);
          }}
          applicant={applicant}
          applicationId={applicationId || 0}
          verificationId={verificationId || 0}
          opened
          incomeId={Number(showReverifyIncomeModal)}
          onClose={() => {
            setShowReverifyIncomeModal(false);
          }}
          income={incomes.find((i) => i.id === showReverifyIncomeModal)}
        />
      )}
    </div>
  );
};
